import clsx from 'clsx'
import { CircleCheckIcon, XIcon } from 'lucide-react'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Button, TextLink, Tooltip } from 'slab'

import { emitEmployerOnboardingEvent } from '../../utils'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'
import { type CoverageLevel } from '../types'

export type CoverageLevelOption = {
  value: CoverageLevel
  label: string
  description?: string
  pills?: readonly string[]
  disabledTooltip?: string
}

export type CoverageLevelSelectorProps = {
  selectedCoverageLevel: CoverageLevel
  onSelectedCoverageLevelChange: (level: CoverageLevel) => void
  notifyChange: (level: CoverageLevel) => void
  coverageLevelOptions: ReadonlyArray<CoverageLevelOption>
  selectedOption?: CoverageLevelOption
  resetFilterValues: () => void
}

export function CoverageLevelSelector ({ selectedCoverageLevel, onSelectedCoverageLevelChange, notifyChange, coverageLevelOptions, selectedOption, resetFilterValues }: CoverageLevelSelectorProps) {
  const { isLoading, coverageLevelPills, hasLowestCostBenchmark, hasBetterBenefitsBenchmark } = useSelfServeExplorerContext()

  const isOptionDisabled = (optionValue: CoverageLevel) => {
    if (optionValue === 'lowest_cost') {
      return !hasLowestCostBenchmark
    }
    if (optionValue === 'better_benefits') {
      return !hasBetterBenefitsBenchmark
    }
    return false
  }

  const getDisabledTooltip = (optionValue: CoverageLevel) => {
    if (optionValue === 'lowest_cost') {
      return 'We don&apos;t have any lower cost strategies available based on your current benefits'
    }
    if (optionValue === 'better_benefits') {
      return (
        <>
          Your current benefits are already tip-top! Great work! Want to offer more?{' '}
          <TextLink
            href='https://thatch.chilipiper.com/round-robin/ssb'
            target='_blank'
            rel='noopener noreferrer'
            color='inherit'
          >
            Reach out
          </TextLink>{' '}
          to the Thatch sales team
        </>
      )
    }
    return ''
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex justify-between items-center'>
        <h2 className='heading-sm'>
          Coverage levels
        </h2>
        <Button variant='tertiary' onClick={resetFilterValues}>
          Reset
        </Button>
      </div>
      <div className='flex flex-col md:flex-row rounded-2xl border border-gray-200 overflow-hidden bg-white p-1'>
        {coverageLevelOptions.map((opt) => {
          const isDisabled = isOptionDisabled(opt.value)
          const disabledTooltip = getDisabledTooltip(opt.value)

          return (
            <Tooltip
              key={opt.value}
              content={disabledTooltip || ''}
              enabled={isDisabled && !!disabledTooltip}
            >
              <button
                type='button'
                disabled={isDisabled}
                className={clsx(
                  'flex-1 text-center transition-all duration-200',
                  'flex items-center justify-center px-2 py-2',
                  'body',
                  selectedCoverageLevel === opt.value
                    ? 'bg-thatch-100 border border-thatch-500 text-gray-800 font-semibold rounded-xl'
                    : isDisabled
                      ? 'bg-transparent text-gray-400 cursor-not-allowed'
                      : 'bg-transparent text-gray-700 hover:bg-gray-50'
                )}
                onClick={() => {
                  if (!isDisabled) {
                    const newCoverageLevel = opt.value
                    onSelectedCoverageLevelChange(newCoverageLevel)
                    notifyChange(newCoverageLevel)

                    if (newCoverageLevel === 'lowest_cost') {
                      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage lowest cost selected', {})
                    } else if (newCoverageLevel === 'best_fit') {
                      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage best fit selected', {})
                    } else if (newCoverageLevel === 'better_benefits') {
                      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage better benefits selected', {})
                    }
                  }
                }}
              >
                {opt.label}
              </button>
            </Tooltip>
          )
        })}
      </div>
      {isLoading && (
        <div className='space-y-4'>
          <SkeletonItem heightClass='h-6' widthClass='w-3/4' />
          <SkeletonItem heightClass='h-8' widthClass='w-full' />
        </div>
      )}
      {selectedOption && !isLoading && (
        <div className='text-center'>
          {selectedOption.description}
          {coverageLevelPills && coverageLevelPills.length > 0 && (
            <div className='flex flex-wrap gap-2 justify-center mt-3'>
              {coverageLevelPills.map((pill, idx: number) => (
                <span
                  key={idx}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full',
                    pill.isPositive
                      ? 'bg-green-50/60 text-green-700'
                      : 'bg-red-50/60 text-red-700'
                  )}
                >
                  {pill.isPositive
                    ? (
                      <CircleCheckIcon className='w-4 h-4 text-green-400' />
                      )
                    : (
                      <XIcon className='w-4 h-4 text-red-400' />
                      )}

                  {pill.text}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
